// Wheel of Fortune — fully client-side (no Admin API calls anywhere in this
// file). Discount codes and free-product claims point at things the
// merchant pre-created once in Shopify Admin; this code only ever reveals
// or applies them. Attempt tracking lives in localStorage + a cookie and is
// a deterrent, not a guarantee — see the note at the top of
// sections/wheel-of-fortune.liquid for why a server is the only way to make
// that airtight, and why this project deliberately doesn't have one.
//
// The spin limit resets per the section's "Spin limit resets" setting
// (data-reset-cadence): Weekly/Daily reset on a fixed CALENDAR boundary,
// deliberately not a rolling window (spinning right at the boundary can't
// buy an extra reset by a few minutes' timing). Custom is the exception —
// an arbitrary N-hour period (data-reset-hours) has no calendar boundary to
// align to in general, so it's a rolling "last N hours from right now"
// window instead. WEEK_START_DAY below is the one place the week boundary
// is defined (0 = Sunday .. 6 = Saturday); it is currently Saturday,
// matching a Gulf-region week. Change that single constant if the store
// needs a different week start — nothing else in this file encodes it.
(function () {
  var STORAGE_KEY = 'flix_wheel_v1';
  var DISMISS_KEY = 'flix_wheel_dismissed_at';
  /** Long enough for a closing overlay's animation to finish first. */
  var MODAL_SETTLE_MS = 450;
  /** 0 = Sunday .. 6 = Saturday. See the header comment above. */
  var WEEK_START_DAY = 6;
  /**
   * How long a recorded spin is kept. Comfortably past the longest reset
   * window (a week), so the limit is never affected — only the record's
   * size, which otherwise grows without bound inside a cookie.
   */
  var SPIN_HISTORY_MS = 31 * 86400000;

  /** The timestamp (local midnight) of the start of the current calendar day. */
  function dayStartMs(now) {
    var d = new Date(now);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  }

  /** The timestamp (local midnight) of the start of the current calendar week. */
  function weekStartMs(now) {
    var d = new Date(now);
    d.setHours(0, 0, 0, 0);
    var day = d.getDay();
    var diff = (day - WEEK_START_DAY + 7) % 7;
    d.setDate(d.getDate() - diff);
    return d.getTime();
  }

  /** The current reset window's start, per the section's configured cadence. */
  function resetStartMs(el, now) {
    var cadence = el.dataset.resetCadence;
    if (cadence === 'custom_hours') {
      var hours = parseFloat(el.dataset.resetHours);
      if (!(hours > 0)) hours = 24;
      return now - hours * 3600000;
    }
    return cadence === 'daily' ? dayStartMs(now) : weekStartMs(now);
  }

  function readJSON(key) {
    try {
      var raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function writeJSON(key, value) {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) {}
  }

  function readCookie(name) {
    var match = document.cookie.match('(?:^|; )' + name + '=([^;]*)');
    return match ? decodeURIComponent(match[1]) : null;
  }

  function writeCookie(name, value, days) {
    var expires = new Date(Date.now() + days * 86400000).toUTCString();
    document.cookie = name + '=' + encodeURIComponent(value) + '; expires=' + expires + '; path=/; SameSite=Lax';
  }

  function readState(customerId) {
    var keys = [STORAGE_KEY];
    if (customerId) keys.push(STORAGE_KEY + '_c' + customerId);

    var candidates = [];
    keys.forEach(function (k) {
      var fromLocal = readJSON(k);
      if (fromLocal) candidates.push(fromLocal);
      var fromCookie = readCookie(k);
      if (fromCookie) {
        try { candidates.push(JSON.parse(fromCookie)); } catch (e) {}
      }
    });
    if (!candidates.length) return { firstSpinAt: null, spins: [] };

    // Most restrictive (most spins recorded) wins, in case one storage
    // mechanism was cleared and another wasn't.
    candidates.sort(function (a, b) { return (b.spins || []).length - (a.spins || []).length; });
    return candidates[0];
  }

  function writeState(state, customerId) {
    var keys = [STORAGE_KEY];
    if (customerId) keys.push(STORAGE_KEY + '_c' + customerId);
    keys.forEach(function (k) {
      writeJSON(k, state);
      writeCookie(k, JSON.stringify(state), 30);
    });
  }

  /**
   * When the next spin becomes available, or null while one still is.
   *
   * It is the moment the OLDEST spin in the current window drops out of it,
   * which differs per cadence: a daily or weekly window turns over at a
   * fixed boundary, so every spin returns at once; a rolling custom window
   * returns them one at a time, each exactly its own span after it was
   * taken. Computed rather than stored, so it stays right if the merchant
   * changes the cadence between visits.
   */
  function nextSpinAtMs(el, spinsInWindow, remaining, windowStart) {
    if (remaining > 0 || !spinsInWindow.length) return null;

    var cadence = el.dataset.resetCadence;
    if (cadence === 'custom_hours') {
      var hours = parseFloat(el.dataset.resetHours);
      if (!(hours > 0)) hours = 24;
      var oldest = Math.min.apply(null, spinsInWindow);
      return oldest + hours * 3600000;
    }
    // windowStart is the boundary this window opened on, so the next one
    // opens one calendar day (or week) later. Stepped by date rather than
    // by milliseconds: a day with a clock change is 23 or 25 hours long.
    var next = new Date(windowStart);
    next.setDate(next.getDate() + (cadence === 'daily' ? 1 : 7));
    return next.getTime();
  }

  function spinStatus(el) {
    var maxSpins = parseInt(el.dataset.maxSpins, 10) || 3;
    var customerId = el.dataset.customerId || '';
    var state = readState(customerId);
    var windowStart = resetStartMs(el, Date.now());

    // Only spins recorded within the CURRENT window (day or week, per
    // data-reset-cadence) count against the limit. Older spins stay in the
    // record (useful for support/debugging) but drop out of the count on
    // their own once the window turns over — no explicit reset write is
    // needed.
    var spinsInWindow = (state.spins || []).filter(function (t) {
      return t >= windowStart;
    });

    var remaining = Math.max(0, maxSpins - spinsInWindow.length);

    return {
      state: state,
      remaining: remaining,
      customerId: customerId,
      windowStart: windowStart,
      nextSpinAt: nextSpinAtMs(el, spinsInWindow, remaining, windowStart)
    };
  }

  function recordSpin(el) {
    var customerId = el.dataset.customerId || '';
    var status = spinStatus(el);
    var state = status.state;
    if (!state.firstSpinAt) state.firstSpinAt = Date.now();
    var cutoff = Date.now() - SPIN_HISTORY_MS;
    state.spins = (state.spins || []).filter(function (t) { return t >= cutoff; });
    state.spins.push(Date.now());
    writeState(state, customerId);
  }

  // ---- Confetti (lightweight, no library) ----
  function burstConfetti(colors) {
    var container = document.createElement('div');
    container.className = 'flx-confetti';
    document.body.appendChild(container);
    var count = 60;
    for (var i = 0; i < count; i++) {
      var piece = document.createElement('span');
      var color = colors[i % colors.length];
      var left = Math.random() * 100;
      var delay = Math.random() * 0.3;
      var duration = 1.6 + Math.random() * 1.2;
      var rotate = Math.random() * 360;
      piece.style.cssText =
        'left:' + left + '%;' +
        'background:' + color + ';' +
        'animation-delay:' + delay + 's;' +
        'animation-duration:' + duration + 's;' +
        '--flx-confetti-rotate:' + rotate + 'deg';
      container.appendChild(piece);
    }
    setTimeout(function () { container.remove(); }, 3200);
  }

  // ---- Optional sound (synthesized, no audio assets) ----
  var audioCtx = null;
  function tone(freq, durationMs, type) {
    try {
      audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
      var osc = audioCtx.createOscillator();
      var gain = audioCtx.createGain();
      osc.type = type || 'sine';
      osc.frequency.value = freq;
      gain.gain.value = 0.06;
      osc.connect(gain);
      gain.connect(audioCtx.destination);
      osc.start();
      gain.gain.exponentialRampToValueAtTime(0.0001, audioCtx.currentTime + durationMs / 1000);
      osc.stop(audioCtx.currentTime + durationMs / 1000);
    } catch (e) {}
  }

  class FlixWheel extends HTMLElement {
    connectedCallback() {
      var el = this;
      el._prizes = [];
      try {
        el._prizes = JSON.parse(el.querySelector('[data-flix-wheel-prizes]').textContent);
      } catch (e) {}
      if (!el._prizes.length) return;

      el._dial = el.querySelector('[data-flix-wheel-dial]');
      el._spinBtn = el.querySelector('[data-flix-wheel-spin]');
      el._exhaustedNote = el.querySelector('[data-flix-wheel-exhausted-note]');
      el._spinning = false;

      layoutSegments(el);
      bindEvents(el);
      scheduleAppearance(el);
    }
  }

  // Each label is a horizontal strip anchored at the dial center, rotated
  // to point along its segment's spoke, then translated outward — radial
  // text that reads from the hub toward the rim with no overlap.
  // transform-origin:0 50% puts the pivot at the strip's left-center;
  // top=calc(50% - labelH/2) makes that pivot coincide with the dial center
  // so rotate(N)+translateX(R) correctly places the strip along the spoke.
  function layoutSegments(el) {
    var n = el._prizes.length;
    if (!n) return;
    var segAngle = 360 / n;
    el._segAngle = segAngle; // set before any early return so spin() works

    var colors = [
      el.dataset.colorA || '#0a0806',
      el.dataset.colorB || '#ff7a1a',
      el.dataset.colorC || '#f7efe6'
    ];

    // 1. Conic-gradient background: hard stops = crisp segment edges
    var stops = [];
    for (var i = 0; i < n; i++) {
      stops.push(colors[i % colors.length] + ' ' + (i * segAngle) + 'deg ' + ((i + 1) * segAngle) + 'deg');
    }
    el._dial.style.background = 'conic-gradient(' + stops.join(',') + ')';

    // 2. Measure — only valid after the element is visible
    var rect = el._dial.getBoundingClientRect();
    var radius = rect.width / 2;
    if (radius <= 0) return; // still hidden; show() will call again after unhiding

    // 3. SVG overlay: 3D depth gradient + gold divider lines
    var oldSvg = el._dial.querySelector('.flx-wheel-svg');
    if (oldSvg) oldSvg.remove();

    var NS = 'http://www.w3.org/2000/svg';
    var D = radius * 2;
    var svg = document.createElementNS(NS, 'svg');
    svg.classList.add('flx-wheel-svg');
    svg.setAttribute('viewBox', '0 0 ' + D + ' ' + D);
    svg.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;pointer-events:none;z-index:2;border-radius:50%';

    var defs = document.createElementNS(NS, 'defs');
    var rg = document.createElementNS(NS, 'radialGradient');
    rg.id = 'flxWhlDepth';
    rg.setAttribute('cx', '50%'); rg.setAttribute('cy', '50%'); rg.setAttribute('r', '50%');
    [
      ['0%',   'rgba(255,255,255,0.22)'],
      ['35%',  'rgba(255,255,255,0.05)'],
      ['70%',  'rgba(0,0,0,0.06)'],
      ['100%', 'rgba(0,0,0,0.34)']
    ].forEach(function (s) {
      var st = document.createElementNS(NS, 'stop');
      st.setAttribute('offset', s[0]); st.setAttribute('stop-color', s[1]); rg.appendChild(st);
    });
    defs.appendChild(rg);
    svg.appendChild(defs);

    // Depth circle
    var dc = document.createElementNS(NS, 'circle');
    dc.setAttribute('cx', radius); dc.setAttribute('cy', radius); dc.setAttribute('r', radius);
    dc.setAttribute('fill', 'url(#flxWhlDepth)');
    svg.appendChild(dc);

    // Gold divider lines from center to rim at every segment boundary
    for (var j = 0; j < n; j++) {
      var aRad = j * segAngle * Math.PI / 180; // clockwise-from-top in radians
      var lx = (radius + radius * Math.sin(aRad)).toFixed(2);
      var ly = (radius - radius * Math.cos(aRad)).toFixed(2);
      var ln = document.createElementNS(NS, 'line');
      ln.setAttribute('x1', radius); ln.setAttribute('y1', radius);
      ln.setAttribute('x2', lx);    ln.setAttribute('y2', ly);
      ln.setAttribute('stroke', 'rgba(255,210,75,0.78)');
      ln.setAttribute('stroke-width', '2.5');
      svg.appendChild(ln);
    }
    el._dial.appendChild(svg);

    // 4. Position labels radially along each segment's spoke
    var labelH  = 22;
    var innerR  = radius * 0.22; // gap between hub and text start
    var textW   = radius * 0.64; // length of text strip along spoke
    var fontSize = Math.max(8, Math.min(13, 15 - n * 0.45));

    el.querySelectorAll('.flx-wheel-segment-label').forEach(function (lbl) {
      var idx = parseInt(lbl.dataset.index, 10);
      var midDeg = idx * segAngle + segAngle / 2; // center angle of this segment

      // CSS rotate(0) = horizontal (3 o'clock), but segments measure clockwise
      // from 12 o'clock, so we subtract 90° to align correctly.
      // For the bottom half (midDeg > 180°) the characters would appear upside
      // down; flip those strips 180° so text stays legible on all segments.
      var rot, tx;
      if (midDeg <= 180) {
        rot = midDeg - 90;
        tx = 'translateX(' + innerR + 'px)';
      } else {
        rot = midDeg + 90;
        tx = 'translateX(-' + (innerR + textW) + 'px)';
      }

      lbl.style.top = 'calc(50% - ' + (labelH / 2) + 'px)';
      lbl.style.height = labelH + 'px';
      lbl.style.width = textW + 'px';
      lbl.style.transform = 'rotate(' + rot + 'deg) ' + tx;

      var span = lbl.querySelector('span');
      if (span) {
        var bi = idx % colors.length;
        // White on dark (color_a), very dark on orange or cream
        span.style.color = bi === 0 ? '#ffffff' : (bi === 1 ? '#160d00' : '#2a1500');
        span.style.textShadow = bi === 0 ? '0 1px 4px rgba(0,0,0,.9)' : '0 1px 2px rgba(0,0,0,.3)';
        span.style.fontSize = fontSize + 'px';
      }
    });
  }

  function bindEvents(el) {
    el.querySelector('[data-flix-wheel-close]').addEventListener('click', function () { dismiss(el); });
    el.querySelector('[data-flix-wheel-backdrop]').addEventListener('click', function () { dismiss(el); });
    var dismissBtn = el.querySelector('[data-flix-wheel-dismiss]');
    if (dismissBtn) dismissBtn.addEventListener('click', function () { dismiss(el); });

    el._spinBtn.addEventListener('click', function () { spin(el); });

    var copyBtn = el.querySelector('[data-flix-wheel-copy]');
    if (copyBtn) {
      copyBtn.addEventListener('click', function () {
        var codeEl = el.querySelector('[data-flix-wheel-code]');
        if (!codeEl) return;
        navigator.clipboard.writeText(codeEl.textContent.trim()).then(function () {
          var original = copyBtn.textContent;
          copyBtn.textContent = copyBtn.dataset.copiedText || '✓';
          setTimeout(function () { copyBtn.textContent = original; }, 1600);
        }).catch(function () {});
      });
    }

    var claimBtn = el.querySelector('[data-flix-wheel-claim-product]');
    if (claimBtn) {
      claimBtn.addEventListener('click', function () { claimFreeProduct(el); });
    }
  }

  function scheduleAppearance(el) {
    // ?wheel=show in the URL bypasses delay + dismiss cooldown for testing
    var forceShow = new URLSearchParams(window.location.search).get('wheel') === 'show';

    var delaySeconds = forceShow ? 0 : (parseFloat(el.dataset.delay) || 0);
    var exitIntentEnabled = el.dataset.exitIntent === 'true';
    var dismissHours = parseFloat(el.dataset.dismissHours) || 24;

    if (!forceShow) {
      var dismissedAt = parseInt(readJSON(DISMISS_KEY) || readCookie(DISMISS_KEY) || '0', 10);
      if (dismissedAt && Date.now() - dismissedAt < dismissHours * 3600000) return;
    }

    var shown = false;

    // A promotion must not interrupt something the visitor opened themselves.
    //
    // This wheel sits at z-index 260 and the Account overlay at 110, so a
    // timer firing mid-sign-in put the wheel on top of it — over the code
    // entry screen, in a real staging run. The two also both write
    // document.body.style.overflow, so whichever closed second would hand
    // the page back a scroll lock the other still wanted.
    //
    // Raising the Account overlay instead would only move the collision to
    // the next modal: the theme stacks cart 100, account 110, search 200,
    // lightbox 250, wheel 260, and those numbers are a deliberate order.
    // So the wheel waits its turn rather than arguing about the order.
    //
    // `data-flix-modal-open` is the theme's existing signal, set by the
    // shared counter in flix-modal-utils.js that cart, search, lightbox and
    // the Account overlay already all report to (the counter is why one
    // modal closing does not clear it while another is still up). Nothing
    // here knows what a modal IS, only that one is on screen.
    function aModalIsOpen() {
      return document.documentElement.hasAttribute('data-flix-modal-open');
    }

    var waitingForModal = null;
    function showWhenNothingElseIsOpen() {
      if (shown) return;
      if (!aModalIsOpen()) {
        show();
        return;
      }
      if (waitingForModal) return;
      // Deliberately not marked shown: the appearance is deferred, not spent.
      waitingForModal = new MutationObserver(function () {
        if (aModalIsOpen()) return;
        waitingForModal.disconnect();
        waitingForModal = null;
        // A short settle so the wheel does not animate in over the tail of
        // the overlay's own closing animation.
        setTimeout(show, MODAL_SETTLE_MS);
      });
      waitingForModal.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['data-flix-modal-open'],
      });
    }

    function show() {
      if (shown) return;
      shown = true;
      if (spinStatus(el).remaining <= 0) {
        el._spinBtn.hidden = true;
        el._exhaustedNote.hidden = false;
      }
      el.hidden = false;
      document.body.style.overflow = 'hidden';
      // getBoundingClientRect returns 0 while the element is hidden, so
      // the SVG overlay and label positions need to be computed now that
      // the element has real dimensions.
      layoutSegments(el);
    }

    setTimeout(showWhenNothingElseIsOpen, delaySeconds * 1000);

    if (exitIntentEnabled) {
      var onLeave = function (e) {
        if (e.clientY <= 0) {
          // Same deferral: leaving the window with the account panel open
          // is not an invitation to cover it.
          showWhenNothingElseIsOpen();
          document.removeEventListener('mouseout', onLeave);
        }
      };
      document.addEventListener('mouseout', onLeave);
    }
  }

  function dismiss(el) {
    el.hidden = true;
    document.body.style.overflow = '';
    writeJSON(DISMISS_KEY, Date.now());
    writeCookie(DISMISS_KEY, String(Date.now()), 2);
  }

  function setView(el, name) {
    el.querySelectorAll('.flx-wheel-view').forEach(function (v) {
      v.hidden = v.dataset.flixWheelView !== name;
    });
  }

  function pickPrize(prizes) {
    var total = prizes.reduce(function (sum, p) { return sum + (parseFloat(p.weight) || 0); }, 0);
    if (total <= 0) return { index: 0, prize: prizes[0] };
    var r = Math.random() * total;
    var acc = 0;
    for (var i = 0; i < prizes.length; i++) {
      acc += parseFloat(prizes[i].weight) || 0;
      // Strict: r is in [0, total), so "<" gives each prize exactly its
      // share and can never land on one whose weight is zero.
      if (r < acc) return { index: i, prize: prizes[i] };
    }
    return { index: prizes.length - 1, prize: prizes[prizes.length - 1] };
  }

  function spin(el) {
    if (el._spinning) return;

    var status = spinStatus(el);
    if (status.remaining <= 0) {
      el._spinBtn.hidden = true;
      el._exhaustedNote.hidden = false;
      return;
    }

    el._spinning = true;
    el._spinBtn.disabled = true;

    var picked = pickPrize(el._prizes);
    var segAngle = el._segAngle;
    var targetCenter = picked.index * segAngle + segAngle / 2;
    var jitter = (Math.random() - 0.5) * (segAngle * 0.6);
    var extraSpins = 5 + Math.floor(Math.random() * 2);
    var currentRotation = el._currentRotation || 0;
    // Land targetCenter under the fixed top pointer: rotate so that point
    // reaches 0deg, plus full spins for visual effect, continuing forward
    // from wherever the dial currently sits rather than resetting to 0.
    var finalRotation = currentRotation - (currentRotation % 360) + extraSpins * 360 + (360 - targetCenter) + jitter;

    el._currentRotation = finalRotation;
    el._dial.style.transition = 'transform 4.2s cubic-bezier(.17,.67,.16,.99)';
    el._dial.style.transform = 'rotate(' + finalRotation + 'deg)';

    if (el.dataset.enableSound === 'true') tone(440, 120, 'square');

    setTimeout(function () {
      el._spinning = false;
      recordSpin(el);
      revealPrize(el, picked.prize);
    }, 4300);
  }

  function revealPrize(el, prize) {
    var confettiEnabled = el.dataset.enableConfetti === 'true';
    var soundEnabled = el.dataset.enableSound === 'true';

    if (prize.type === 'discount' && prize.discountCode) {
      if (confettiEnabled) burstConfetti([el.dataset.colorA, el.dataset.colorB, el.dataset.colorC]);
      if (soundEnabled) { tone(523, 150, 'triangle'); setTimeout(function () { tone(659, 200, 'triangle'); }, 150); }
      var codeEl = el.querySelector('[data-flix-wheel-code]');
      var labelEl = el.querySelector('[data-flix-wheel-view="win-discount"] [data-flix-wheel-win-label]');
      var shopLink = el.querySelector('[data-flix-wheel-shop-link]');
      if (codeEl) codeEl.textContent = prize.discountCode;
      if (labelEl) labelEl.textContent = prize.label;
      if (shopLink) shopLink.href = window.location.origin + '/discount/' + encodeURIComponent(prize.discountCode);
      setView(el, 'win-discount');
    } else if (prize.type === 'free_product' && prize.variantId) {
      if (confettiEnabled) burstConfetti([el.dataset.colorA, el.dataset.colorB, el.dataset.colorC]);
      if (soundEnabled) { tone(523, 150, 'triangle'); setTimeout(function () { tone(659, 200, 'triangle'); }, 150); }
      el._claimPrize = prize;
      var prodLabelEl = el.querySelector('[data-flix-wheel-view="win-product"] [data-flix-wheel-win-label]');
      if (prodLabelEl) prodLabelEl.textContent = prize.label;
      setView(el, 'win-product');
    } else {
      setView(el, 'lose');
    }
  }

  function claimFreeProduct(el) {
    var prize = el._claimPrize;
    if (!prize || !prize.variantId) return;
    var btn = el.querySelector('[data-flix-wheel-claim-product]');
    btn.disabled = true;

    var addPromise = window.FlixCartAPI && window.FlixCartAPI.add
      ? window.FlixCartAPI.add(prize.variantId, 1)
      : fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: prize.variantId, quantity: 1 }] })
        });

    addPromise.then(function () {
      var target = '/discount/' + encodeURIComponent(prize.discountCode) + '?redirect=/checkout';
      window.location.href = target;
    }).catch(function () {
      btn.disabled = false;
    });
  }

  if (!customElements.get('flix-wheel')) {
    customElements.define('flix-wheel', FlixWheel);
  }

  // Recompute label positions on resize — the dial size changes on orientation
  // change or window resize, so we need fresh getBoundingClientRect measurements.
  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      var el = document.querySelector('flix-wheel:not([hidden])');
      if (el && el._dial) layoutSegments(el);
    }, 200);
  });
  // ---- Reusable core, for the Hero wheel ----
  //
  // The Hero wheel (assets/flix-hero-wheel.js) is a different presentation of
  // the SAME wheel: same prize blocks, same weights, same attempt tracking.
  // Exposing these three rather than reimplementing them is the point — a
  // second weighted pick or a second storage key would be a second answer to
  // the question of what somebody won and whether they may spin again.
  //
  // Behaviour unchanged: these are the functions this file already used.
  window.FlixWheelCore = {
    pickPrize: pickPrize,
    spinStatus: spinStatus,
    recordSpin: recordSpin
  };

})();
